import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { listPhotographs, imageUrl } from "../lib/api";
import NoSaveImage from "../components/NoSaveImage";
import Seo from "../components/Seo";

const SCROLL_KEY = "gallery-scroll-y";

export default function Gallery() {
  const [photos, setPhotos] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    listPhotographs().then(setPhotos);
  }, []);

  // Restore scroll after the grid has painted.
  useEffect(() => {
    if (!photos.length) return;
    const saved = sessionStorage.getItem(SCROLL_KEY);
    if (saved) {
      // wait a frame for images to lay out before restoring
      const y = parseInt(saved, 10);
      requestAnimationFrame(() =>
        requestAnimationFrame(() => window.scrollTo({ top: y, behavior: "instant" })),
      );
    }
  }, [photos.length]);

  function openPhoto(id) {
    sessionStorage.setItem(SCROLL_KEY, String(window.scrollY));
    // Gallery is a fresh doorway — clear any prior wander trail so
    // "← Back" doesn't quietly retrace into an unrelated earlier session.
    sessionStorage.removeItem("explore-trail");
    navigate(`/explore/${id}`, { state: { fromGallery: true } });
  }

  return (
    <main
      data-testid="gallery-page"
      className="min-h-screen bg-[color:var(--bg)] text-white pt-32 pb-24 grain"
    >
      <Seo
        title="Gallery"
        description="A visual index of every photograph in Carol Lines' labyrinth. Select any image to enter the exploration there."
        path="/gallery"
      />
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <header className="mb-12 md:mb-16 max-w-2xl">
          <h1
            data-testid="gallery-heading"
            className="font-serif-display font-light text-3xl md:text-4xl leading-tight tracking-tight text-white/85"
          >
            Gallery
          </h1>
          <p className="mt-3 font-serif-display italic text-white/50 text-sm md:text-base">
            An index into the exploration. Select any photograph to enter there.
          </p>
        </header>

        <ul
          data-testid="gallery-grid"
          className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 md:gap-10"
        >
          {photos.map((p, i) => (
            <motion.li
              key={p.id}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{
                delay: (i % 6) * 0.05,
                duration: 0.7,
                ease: [0.2, 0.7, 0.1, 1],
              }}
            >
              <button
                type="button"
                onClick={() => openPhoto(p.id)}
                data-testid={`gallery-thumb-${p.id}`}
                aria-label={p.alt || p.title || "Photograph"}
                className="group block w-full text-left"
              >
                <div className="overflow-hidden bg-black/40">
                  <NoSaveImage
                    src={imageUrl(p.image)}
                    alt={p.alt || ""}
                    loading="lazy"
                    className="w-full h-auto block transition-all duration-[900ms] ease-out group-hover:opacity-90 group-hover:scale-[1.02]"
                  />
                </div>
              </button>
            </motion.li>
          ))}
        </ul>

        <div className="mt-24 border-t border-white/[0.06] pt-8 flex items-center justify-between text-xs text-white/40 font-serif-display italic">
          <span>Personal work · Not for sale</span>
          <a
            href="/contact"
            data-testid="gallery-contact-link"
            className="hover:text-white/75 transition-colors"
          >
            Contact
          </a>
        </div>
      </div>
    </main>
  );
}
